// Voice modal state machine (docs/mockups/ai-voice-modal-mockup.html).
// Five states, four visuals, zero ambiguity: speaking is a loudspeaker, listening
// is a mic, and they can never be conflated. The reducer is pure — all side
// effects (start/stop recognition, stop TTS) live in the dashboard's dispatcher.
//
//   closed ── OPEN ──▶ listening ── TRANSCRIPT_FINAL ──▶ thinking ── REPLY_READY ──▶ speaking
//                          ▲                                 │ CANCEL/ERROR             │
//                          │◀───────── SPEAK ─────────── paused ◀───── STOP_AUDIO ──────┤
//                          │◀──────────────────────── AUDIO_ENDED ──────────────────────┘
//   any state ── DISMISS ──▶ closed  (swipe-down / scrim tap; audio + mic must die)

export const VOICE_EVENTS = {
  OPEN: 'OPEN',
  TRANSCRIPT_FINAL: 'TRANSCRIPT_FINAL',
  REPLY_READY: 'REPLY_READY',
  AUDIO_ENDED: 'AUDIO_ENDED',
  STOP_AUDIO: 'STOP_AUDIO',
  SPEAK: 'SPEAK',
  CANCEL: 'CANCEL',
  ERROR: 'ERROR',
  DISMISS: 'DISMISS',
};

const TRANSITIONS = {
  closed:    { OPEN: 'listening' },
  listening: { TRANSCRIPT_FINAL: 'thinking', ERROR: 'paused', DISMISS: 'closed' },
  thinking:  { REPLY_READY: 'speaking', CANCEL: 'paused', ERROR: 'paused', DISMISS: 'closed' },
  speaking:  { STOP_AUDIO: 'paused', SPEAK: 'listening', AUDIO_ENDED: 'listening', DISMISS: 'closed' },
  paused:    { SPEAK: 'listening', DISMISS: 'closed' },
};

export function voiceReducer(state, event) {
  const row = TRANSITIONS[state];
  return (row && row[event]) || state;
}

// Sentence chunking for pipelined TTS. The server synthesizes a WHOLE request to a
// file before responding, so first-sound latency scales with text length — a long
// reply meant ~20s of silence. Chunk 1 is the FIRST SENTENCE ALONE (fastest possible
// start); the rest group up to maxLen and prefetch while earlier chunks play.
export function ttsChunks(text, maxLen = 240) {
  const clean = String(text || '').trim();
  if (!clean) return [];
  const sentences = clean.match(/[^.!?\n]+[.!?]*[\s\n]*/g) || [clean];
  const pieces = [];
  sentences.forEach((s) => {
    let p = s;
    while (p.length > maxLen) { pieces.push(p.slice(0, maxLen)); p = p.slice(maxLen); }
    if (p.trim()) pieces.push(p);
  });
  if (!pieces.length) return [];
  // The sentence regex treats a list marker ("1.") as a sentence END — leaving a
  // chunk dangling on "…: 1." and starting the next mid-item. Move a trailing bare
  // marker onto the FOLLOWING piece instead.
  for (let i = 0; i < pieces.length - 1; i++) {
    const m = pieces[i].match(/(^|\s)(\d+[.)])(\s*)$/);
    if (m) {
      pieces[i] = pieces[i].slice(0, pieces[i].length - m[2].length - m[3].length);
      pieces[i + 1] = m[2] + ' ' + pieces[i + 1];
      if (!pieces[i].trim()) { pieces.splice(i, 1); i--; }
    }
  }
  const chunks = [pieces[0].trim()];
  let cur = '';
  pieces.slice(1).forEach((p) => {
    if (cur && (cur.length + p.length) > maxLen) { chunks.push(cur.trim()); cur = ''; }
    cur += p;
  });
  if (cur.trim()) chunks.push(cur.trim());
  return chunks;
}
